
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import * as t from "io-ts";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { PropertiesDataV } from "@/types/tree";
import { DesignProvider } from "@/types/design-library";
import { ElementPresetsV } from "@/types/presets";
import { VariableListV } from "@/oxygen/variables/lib/types";
import { OxySelectorV } from "@/oxygen/classes/lib/types";
import { configStore, globalStore, uiStore } from "@/store/store";

import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import Icon from "@/components/UI/Icon.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import NotificationLogger from "@/lib/notifications/notifications";
import LoadingSpinner from "@/components/Reusable/LoadingSpinner.vue";

const IMPORT_TYPES = {
  globalSettings: {
    key: "globalSettings",
    label: "Global Settings",
    action: "breakdance_get_external_global_settings",
    decoder: PropertiesDataV,
    storeKey: "globalSettingsLastImportedFromUrl",
    setter: (data: any) => globalStore.setGlobalSettings(data?.globalSettings),
    uiPanel: "globalSettings"
  },
  designPresets: {
    key: "designPresets",
    label: "Design Presets",
    action: "breakdance_get_external_design_presets",
    decoder: ElementPresetsV,
    storeKey: "designPresetsLastImportedFromUrl",
    setter: (data: any) => globalStore.setPresets(data),
    uiPanel: "presets"
  },
  variables: {
    key: "variables",
    label: "Variables",
    action: "breakdance_get_external_variables",
    decoder: t.type({
      variables: VariableListV,
      collections: t.array(t.string)
    }),
    storeKey: "variablesLastImportedFromUrl",
    setter: (data: any) => {
      globalStore.setVariables(data.variables);
      globalStore.setVariablesCollections(data.collections);
    },
    uiPanel: "variables"
  },
  oxySelectors: {
    key: "oxySelectors",
    label: "Selectors",
    action: "breakdance_get_external_oxy_selectors",
    decoder: t.type({
      selectors: t.array(OxySelectorV),
      collections: t.array(t.string)
    }),
    storeKey: "oxySelectorsLastImportedFromUrl",
    setter: (data: any) => {
      console.log("importing selectors", data);
      globalStore.setOxygenSelectors(data.selectors);
      globalStore.setOxygenSelectorsCollections(data.collections);
    },
    uiPanel: null
  }
} as const;

@Component({
  components: {
    Icon,
    NiceButton,
    LoadingSpinner
  }
})
export default class DesignLibraryDependencyWarning extends Vue {
  @Prop({ default: null })
  provider!: DesignProvider | null;

  @Prop({ default: false })
  reliesOnGlobalSettings!: boolean;

  @Prop({ default: false })
  reliesOnDesignPresets!: boolean;

  loading = false;
  showImportScreen = false;

  selectedItems = this.getDefaultSelectedItems();

  get isInlineImportEnabled() {
    if (!this.provider) return false;
    if (this.provider.isLocal) return false;
    if (this.wasPreviouslyImported) return true;
    return this.hasAvailableImportTypes;
  }

  get wasPreviouslyImported() {
    const providerUrl = this.provider?.url;
    const providerData = this.providerData;

    const importUrlKeys = [
      "globalSettingsLastImportedFromUrl",
      "designPresetsLastImportedFromUrl",
      "variablesLastImportedFromUrl",
      "oxySelectorsLastImportedFromUrl"
      // "fullSiteLastImportedFromUrl"
    ];

    return importUrlKeys.some(
      key => (providerData as any)?.[key] === providerUrl
    );
  }

  get providerData() {
    return configStore.designLibrary;
  }

  get hasAvailableImportTypes() {
    return (
      this.isGlobalSettingsEnabled() ||
      this.isDesignPresetsEnabled() ||
      this.isVariablesEnabled() ||
      this.isOxySelectorsEnabled()
    );
  }

  get availableImportTypesCount() {
    return [
      this.isGlobalSettingsEnabled(),
      this.isDesignPresetsEnabled(),
      this.isVariablesEnabled(),
      this.isOxySelectorsEnabled()
    ].filter(Boolean).length;
  }

  get hasSelectedItems() {
    return Object.values(this.selectedItems).some(selected => selected);
  }

  get items() {
    const availableItems = [];

    if (this.isGlobalSettingsEnabled()) {
      availableItems.push("Global Settings");
    }

    if (this.isDesignPresetsEnabled()) {
      availableItems.push("Design Presets");
    }

    if (this.isVariablesEnabled()) {
      availableItems.push("Variables");
    }

    if (this.isOxySelectorsEnabled()) {
      availableItems.push("Selectors");
    }

    return this.formatItemsList(availableItems);
  }

  getDefaultSelectedItems() {
    return {
      globalSettings: this.isGlobalSettingsEnabled(),
      designPresets: this.isDesignPresetsEnabled(),
      variables: this.isVariablesEnabled(),
      oxySelectors: this.isOxySelectorsEnabled()
    };
  }

  isGlobalSettingsEnabled() {
    return (
      this.providerData?.isGlobalSettingsEnabled && this.reliesOnGlobalSettings
    );
  }

  isDesignPresetsEnabled() {
    return (
      this.providerData?.isDesignPresetsEnabled && this.reliesOnDesignPresets
    );
  }

  isVariablesEnabled() {
    return this.providerData?.isVariablesEnabled;
  }

  isOxySelectorsEnabled() {
    return this.providerData?.isOxySelectorsEnabled;
  }

  formatItemsList(items: string[]) {
    if (items.length <= 1) return items.join("");
    if (items.length === 2) return items[0] + " and " + items[1];
    return items.slice(0, -1).join(", ") + ", and " + items[items.length - 1];
  }

  getProviderDisplayName() {
    return this.provider?.name || this.provider?.url || "Unknown";
  }

  resetSelectedItems() {
    this.selectedItems = this.getDefaultSelectedItems();
  }

  toggleImportScreen() {
    this.showImportScreen = !this.showImportScreen;
    if (this.showImportScreen) {
      this.resetSelectedItems();
    }
  }

  async importSelectedItems() {
    if (this.provider?.isLocal) return;

    this.loading = true;

    const importPromises = Object.entries(this.selectedItems)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, selected]) => selected)
      .map(([typeKey]) =>
        this.importType(typeKey as keyof typeof IMPORT_TYPES)
      );

    await Promise.all(importPromises);

    this.loading = false;
    this.showImportScreen = false;
    this.resetSelectedItems();
  }

  async importType(typeKey: keyof typeof IMPORT_TYPES) {
    const type = IMPORT_TYPES[typeKey];
    const url = this.provider?.url;

    if (!type.action) return;
    if (!url) return;

    const result = pipe(
      await makeWordPressAjaxRequest({
        action: type.action,
        decoder: type.decoder!,
        friendlyName: `${type.label} for "${url}"`,
        dataToPost: { url }
      }),
      fold(
        () => false,
        data => {
          if (type.storeKey) {
            configStore.setDesignLibrary({
              ...configStore.designLibrary,
              [type.storeKey]: url
            });
          }
          return data;
        }
      )
    );

    if (result === false || result === null) {
      NotificationLogger.log.errorMessage(
        `Error importing ${type.label} for "${this.getProviderDisplayName()}"`
      );
      return;
    }

    type.setter?.(result);

    if (type.uiPanel) {
      uiStore.setShouldPanelLoadFreshDataIntoStoreOnceOpened(
        type.uiPanel,
        false
      );
    }

    NotificationLogger.log.message(
      `Imported ${type.label} from "${this.getProviderDisplayName()}"`
    );
  }
}
